import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  merchantWriteCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { mutateIdempotent } from "@/core/api/mutate-idempotent";
import { logger } from "@/core/utils/func/logger";
import { merchantEndpoints } from "@/features/merchant/api/endpoints";
import type {
  CreateMerchantPayload,
  MerchantDto,
} from "@/features/merchant/api/dto";

/**
 * Crée un marchand (`POST /Merchants/add`) — idempotence OBLIGATOIRE
 * (guide §4.7). Le scope conserve la même clé entre retries automatiques
 * de la MÊME tentative ; 422 (clé + corps différent) régénère la clé car
 * l'utilisateur a modifié le formulaire = nouvelle tentative métier.
 */
export const useCreateMerchant = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["merchant", "merchants", "create"],
    mutationFn: (body: CreateMerchantPayload) =>
      mutateIdempotent<MerchantDto>({
        scopeId: "merchant.merchants.add",
        url: merchantEndpoints.merchants.add,
        body,
      }),
    onSuccess: () => {
      return invalidateAfterMutation(queryClient, merchantWriteCacheKeys(), {
        resetListPage: true,
      });
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useCreateMerchant failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "merchant" }
      );
    },
  });
};
